import React, { useRef, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

// Import custom hooks
import { useResponsive } from '../../hooks/useResponsive';
import { useConversation } from '../../hooks/useConversation';
import { useKeyboardVisibility } from '../../hooks/useKeyboardVisibility';

// Import components
import ConversationContainer from '../../components/home/ConversationContainer';
import InputArea from '../../components/home/InputArea';

/**
 * Main Home page component for the application
 * @returns {JSX.Element} The Home page component
 */
const Home = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const conversationContainerRef = useRef(null);
  const inputRef = useRef(null);
  const inputContainerRef = useRef(null);
  const MAX_CONTEXT_TOKENS = 4000; // Adjust based on model's context window

  // Initialize custom hooks
  const { isOnPhone } = useResponsive();

  const {
    query,
    setQuery,
    responses,
    loadingIndex,
    conversationId,
    detectedGoLinks,
    feedbackSubmitted,
    submittingFeedback,
    totalContextTokens,
    arrowCycle,
    charCount,
    MAX_INPUT_LENGTH,
    latestMessageRef,
    scrollToLatestMessage,
    handleNewConversation,
    handleInputChange,
    handleKeyPress,
    handleSearch,
    handleSearchWithText,
    handleFeedback
  } = useConversation(MAX_CONTEXT_TOKENS);

  // Handle keyboard visibility for mobile devices
  useKeyboardVisibility({
    isOnPhone,
    inputRef,
    inputContainerRef,
    conversationContainerRef
  });

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!currentUser) {
      navigate("/login");
    }
  }, [currentUser, navigate]);

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-white/20 dark:bg-black/20 backdrop-blur-lg h-full rounded overflow-clip border border-white/20 dark:border-white/10 relative shadow-xl">


      {/* Conversation Container */}
      <div className="flex-1 flex flex-col h-full px-6">
        <ConversationContainer
          responses={responses}
          loadingIndex={loadingIndex}
          isOnPhone={isOnPhone}
          currentUser={currentUser}
          detectedGoLinks={detectedGoLinks}
          conversationId={conversationId}
          feedbackSubmitted={feedbackSubmitted}
          submittingFeedback={submittingFeedback}
          handleFeedback={handleFeedback}
          conversationContainerRef={conversationContainerRef}
          latestMessageRef={latestMessageRef}
          scrollToLatestMessage={scrollToLatestMessage}
        />

        {/* Input Area */}
        <InputArea
          query={query}
          handleInputChange={handleInputChange}
          handleKeyPress={handleKeyPress}
          handleSearch={handleSearch}
          charCount={charCount}
          MAX_INPUT_LENGTH={MAX_INPUT_LENGTH}
          arrowCycle={arrowCycle}
          isOnPhone={isOnPhone}
        />
      </div>
    </div>
  );
};

export default Home;